<!-- Drill-Down hierarchy / measures / pivot / formulas — shared by the legacy
     Drill-Down Table layout and the Table & Matrix "Drill-Down" type. -->
<ng-container *ngIf="active">
  <div class="drill-down-dimensions wf-field-drop-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onDrillDimensionFieldDrop($event)">
    <header class="wf-section-header wf-steps-head">
      <h3>{{ host.layoutSectionTitle('dimensions', 'Hierarchy dimensions') }}</h3>
      <button type="button" mat-stroked-button (click)="addDrillDimension()">+ Add Level</button>
    </header>
    <small class="control-hint" *ngIf="host.layoutSectionHint('dimensions')">{{ host.layoutSectionHint('dimensions') }}</small>
    <small class="control-hint" *ngIf="!(isTableMatrixDrilldown)">One dataset drives every level.</small>
    <small class="control-hint" *ngIf="isTableMatrixDrilldown">The primary dataset below drives every level and measure by default — each card can pick a different dataset (client-side computation joins them). The table opens at Level 1; users expand rows to drill down.</small>
    <ng-container *ngTemplateOutlet="datasetPicker"></ng-container>

    <div class="wf-empty" *ngIf="!drillDownDimensions.length">
      No dimensions yet - add Level 1 and map fields like Type Grouping, Global Business Unit, Business Unit.
    </div>

    <div class="wf-step-card wf-field-drop-zone" *ngFor="let dimension of drillDownDimensions; let i = index; let first = first; let last = last" (dragover)="host.allowFieldDrop($event)" (drop)="onDrillDimensionFieldDrop($event, dimension)">
      <div class="wf-step-head">
        <span class="wf-step-index">{{ i + 1 }}</span>

        <ng-select
          #ssWfDimField
          class="ss-select wf-step-field-select"
          [class.is-open]="!!ssWfDimField.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="columnsForDrillRow(dimension)"
          [clearable]="true"
          [searchable]="true"
          [loading]="isDrillRowLoading(dimension)"
          [disabled]="!drillRowHasDataset(dimension)"
          [placeholder]="host.drillColumnPlaceholder('dimension')"
          [ngModel]="dimension.field || null"
          (ngModelChange)="dimension.field = $event || ''; onDrillDimensionChange()"
        ></ng-select>

        <input
          type="text"
          class="wf-step-label"
          [ngModel]="dimension.label"
          (ngModelChange)="dimension.label = $event; onDrillDimensionChange()"
          placeholder="Display name"
          [matTooltip]="i === 0 ? 'Shown as the tree column header' : 'Sent to the backend for this level'"
        />

        <div class="wf-step-actions">
          <button type="button" mat-icon-button [disabled]="first" (click)="moveDrillDimensionUp(i)" matTooltip="Move up" aria-label="Move level up">
            <mat-icon>arrow_upward</mat-icon>
          </button>
          <button type="button" mat-icon-button [disabled]="last" (click)="moveDrillDimensionDown(i)" matTooltip="Move down" aria-label="Move level down">
            <mat-icon>arrow_downward</mat-icon>
          </button>
          <button type="button" mat-icon-button class="row-remove-button" (click)="removeDrillDimension(dimension)" matTooltip="Remove level" aria-label="Remove level">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>

      <!-- NAVIGATION — right under the level's name: click a label of this
           level → open a published dashboard filtered on that row. -->
      <div class="control-row">
        <div class="control-group control-group-full">
          <app-table-cell-config-editor
            [navigationAccordion]="true"
            title="Navigation"
            [config]="dimension.cellConfig"
            [fields]="drillRuleFields"
            (configChange)="onDrillDimensionCellConfigChange(dimension, $event)"
          ></app-table-cell-config-editor>
        </div>
      </div>

      <!-- Optional fixed ordering for THIS level's values. Left blank, the
           key is omitted from the payload entirely and the backend keeps its
           default ordering. -->
      <!-- Merged drill-down + pivot: a level may read from its OWN dataset
           (joined by the flat compute contract in client-side mode). -->
      <div class="control-row" *ngIf="isTableMatrixDrilldown">
        <div class="control-group control-group-full">
          <label>Dataset</label>
          <ng-select
            #ssWfDimDataset
            class="ss-select"
            [class.is-open]="!!ssWfDimDataset.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="host.entityOptions"
            [loading]="host.entitiesLoading"
            [clearable]="true"
            [searchable]="true"
            placeholder="Same as primary"
            [ngModel]="dimension.dataset || host.drillDataset || null"
            (ngModelChange)="onDrillRowDatasetChange(dimension, $event, 'dimension')"
          ></ng-select>
        </div>
      </div>

      <div class="control-row" *ngIf="host.fieldEnabled('dimensions', 'valueOrder')">
        <div class="control-group control-group-full">
          <label>{{ host.fieldLabel('dimensions', 'valueOrder', 'Value order (optional)') }}</label>
          <input
            type="text"
            [ngModel]="dimension.valueOrder"
            (ngModelChange)="dimension.valueOrder = $event; onDrillDimensionChange()"
            placeholder="e.g. Base Options, Additional Options, Routing Cost"
          />
          <small class="control-hint">
            Comma-separated, in the order you want the rows to appear. Leave blank to keep the default order.
          </small>
        </div>
      </div>

      <!-- Wrapped in a control-group like the measure card's checkbox: the
           builder SCSS hides any .control-row WITHOUT a .control-group
           (`.control-row:not(:has(.control-group))`), which silently hid
           this checkbox while "Editable values" on measures still showed. -->
      <div class="control-row" *ngIf="host.fieldEnabled('dimensions', 'editable')">
        <div class="control-group">
          <label class="checkbox-label" matTooltip="Renders this level's names in the tree column as editable text inputs (a rename); Save sends the old + new name through the table's save pipeline">
            <input
              type="checkbox"
              [ngModel]="dimension.editable"
              (ngModelChange)="dimension.editable = $event; onDrillDimensionChange()"
            />
            {{ host.fieldLabel('dimensions', 'editable', 'Editable labels') }}
          </label>
        </div>
      </div>

      <!-- Edit control for the rename: a free-text input, or a dropdown over
           CUSTOM values (they need not exist in the data) and/or the labels
           already in the table at this level. -->
      <div class="control-row" *ngIf="dimension.editable && host.fieldEnabled('dimensions', 'editor')">
        <div class="control-group">
          <label>{{ host.fieldLabel('dimensions', 'editor', 'Edit control') }}</label>
          <ng-select
            #ssWfDimEditor
            class="ss-select"
            [class.is-open]="!!ssWfDimEditor.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="drillEditorKinds"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [ngModel]="dimension.editor || 'input'"
            (ngModelChange)="dimension.editor = $event === 'dropdown' ? 'dropdown' : undefined; onDrillDimensionChange()"
          ></ng-select>
        </div>
      </div>
      <div class="control-row" *ngIf="dimension.editable && dimension.editor === 'dropdown' && host.fieldEnabled('dimensions', 'editor')">
        <div class="control-group control-group-full">
          <label>{{ host.fieldLabel('dimensions', 'dropdownOptions', 'Dropdown values') }}</label>
          <input
            type="text"
            [ngModel]="dimension.dropdownOptions"
            (ngModelChange)="dimension.dropdownOptions = $event; onDrillDimensionChange()"
            placeholder="e.g. Approved, Pending, Rejected"
          />
          <small class="control-hint">
            Comma-separated custom values — they don't have to exist in the data. Leave blank to list only the values from the table.
          </small>
          <label class="checkbox-label" matTooltip="Adds every label currently present at this level to the dropdown, after the custom values">
            <input
              type="checkbox"
              [ngModel]="dimension.dropdownFromData === true"
              (ngModelChange)="dimension.dropdownFromData = $event ? true : undefined; onDrillDimensionChange()"
            />
            {{ host.fieldLabel('dimensions', 'dropdownFromData', 'Also list values from the table') }}
          </label>
        </div>
      </div>
      <!-- GENERIC cell configuration for this level's labels in the tree
           column — the ONE editor shared with the table columns / measures
           (table-cell-config.ts): colours, conditional rules, highlight… -->
      <div class="control-row">
        <div class="control-group control-group-full">
          <app-table-cell-config-editor
            title="Label formatting"
            [showNavigation]="false"
            [config]="dimension.cellConfig"
            [fields]="drillRuleFields"
            valueKind="text"
            (configChange)="onDrillDimensionCellConfigChange(dimension, $event)"
          ></app-table-cell-config-editor>
        </div>
      </div>
    </div>
  </div>
<div class="drill-down-measures wf-field-drop-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onDrillMeasureFieldDrop($event)">
  <header class="wf-section-header wf-steps-head">
    <h3>{{ host.layoutSectionTitle('measures', 'Measures') }}</h3>
    <button type="button" mat-stroked-button (click)="addDrillMeasure()">+ Add Measure</button>
  </header>
  <small class="control-hint" *ngIf="host.layoutSectionHint('measures')">{{ host.layoutSectionHint('measures') }}</small>

  <div class="wf-empty" *ngIf="!drillDownMeasures.length">
    No measures yet — add at least one value column (e.g. DN Price) to show and roll up at every level.
  </div>

  <div class="wf-step-card wf-field-drop-zone" *ngFor="let measure of drillDownMeasures; let i = index; let first = first; let last = last" (dragover)="host.allowFieldDrop($event)" (drop)="onDrillMeasureFieldDrop($event, measure)">
    <div class="wf-step-head">
      <span class="wf-step-index">{{ i + 1 }}</span>

      <ng-select
        #ssWfMeasureField
        class="ss-select wf-step-field-select"
        [class.is-open]="!!ssWfMeasureField.isOpen?.()"
        appendTo="body"
        ngClass="ss-dropdown-panel"
        [items]="columnsForDrillRow(measure)"
        [clearable]="true"
        [searchable]="true"
        [loading]="isDrillRowLoading(measure)"
        [disabled]="!drillRowHasDataset(measure)"
        [placeholder]="host.drillColumnPlaceholder('measure')"
        [ngModel]="measure.field || null"
        (ngModelChange)="measure.field = $event || ''; onDrillMeasureChange()"
      ></ng-select>

      <input
        type="text"
        class="wf-step-label"
        [ngModel]="measure.label"
        (ngModelChange)="measure.label = $event; onDrillMeasureChange()"
        placeholder="Measure label"
      />
<div class="wf-step-actions">
        <button type="button" mat-icon-button [disabled]="first" (click)="moveDrillMeasureUp(i)" matTooltip="Move up" aria-label="Move measure up">
          <mat-icon>arrow_upward</mat-icon>
        </button>
        <button type="button" mat-icon-button [disabled]="last" (click)="moveDrillMeasureDown(i)" matTooltip="Move down" aria-label="Move measure down">
          <mat-icon>arrow_downward</mat-icon>
        </button>
        <button type="button" mat-icon-button class="row-remove-button" (click)="removeDrillMeasure(measure)" matTooltip="Remove measure" aria-label="Remove measure">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- NAVIGATION — right under the measure's name: click a value of this
         measure → open a published dashboard filtered on that row. -->
    <div class="control-group">
      <app-table-cell-config-editor
        [navigationAccordion]="true"
        title="Navigation"
        [config]="measure.cellConfig"
        [fields]="drillRuleFields"
        (configChange)="onDrillMeasureCellConfigChange(measure, $event)"
      ></app-table-cell-config-editor>
    </div>

    <div class="wf-step-body">
      <!-- Merged drill-down + pivot: a measure may come from another dataset. -->
      <div class="control-group" *ngIf="isTableMatrixDrilldown">
        <label>Dataset</label>
        <ng-select
          #ssWfMeasureDataset
          class="ss-select"
          [class.is-open]="!!ssWfMeasureDataset.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="host.entityOptions"
          [loading]="host.entitiesLoading"
          [clearable]="true"
          [searchable]="true"
          placeholder="Same as primary"
          [ngModel]="measure.dataset || host.drillDataset || null"
          (ngModelChange)="onDrillRowDatasetChange(measure, $event, 'measure')"
        ></ng-select>
      </div>
      <div class="control-group" *ngIf="host.fieldEnabled('measures', 'aggregation')">
        <label>{{ host.fieldLabel('measures', 'aggregation', 'Aggregation') }}</label>
        <ng-select
          #ssWfMeasureAgg
          class="ss-select"
          [class.is-open]="!!ssWfMeasureAgg.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="host.measureAggregations"
          bindLabel="label"
          bindValue="value"
          [clearable]="true"
          [ngModel]="measure.aggregation"
          (ngModelChange)="measure.aggregation = $event ?? ''; onDrillMeasureChange()"
        ></ng-select>
      </div>
      <div class="control-group" *ngIf="host.fieldEnabled('measures', 'rollUpStrategy')">
        <label>{{ host.fieldLabel('measures', 'rollUpStrategy', 'Roll-up strategy') }}</label>
        <ng-select
          #ssWfMeasureRollup
          class="ss-select"
          [class.is-open]="!!ssWfMeasureRollup.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="rollUpStrategies"
          bindLabel="label"
          bindValue="value"
          [clearable]="true"
          [ngModel]="measure.rollUpStrategy"
          (ngModelChange)="measure.rollUpStrategy = $event ?? ''; onDrillMeasureChange()"
        ></ng-select>
      </div>
      <div class="control-group" *ngIf="host.fieldEnabled('measures', 'total')">
        <label class="checkbox-label" matTooltip="Adds this measure to a dataset-wide Grand Total row at the bottom of the table">
          <input
            type="checkbox"
            [ngModel]="measure.total"
            (ngModelChange)="measure.total = $event; onDrillMeasureChange()"
          />
          {{ host.fieldLabel('measures', 'total', 'Show in Grand Total') }}
        </label>
      </div>
      <!-- Merged drill-down + pivot: value kind (number vs free-text attribute)
           and, once a pivot column exists, whether this measure fans out per
           column value or stays ONE column (Cost increase %, AOP Target…). -->
      <div class="control-group" *ngIf="isTableMatrixDrilldown">
        <label>Value type</label>
        <ng-select
          #ssWfMeasureKind
          class="ss-select"
          [class.is-open]="!!ssWfMeasureKind.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="drillMeasureKinds"
          bindLabel="label"
          bindValue="value"
          [clearable]="true"
          [ngModel]="measure.kind || 'number'"
          (ngModelChange)="measure.kind = $event === 'text' ? 'text' : undefined; onDrillMeasureChange()"
        ></ng-select>
        <small class="control-hint" *ngIf="measure.kind === 'text'">Shown as text per row — a parent (or a pivot cell spanning several raw rows) lists its distinct values comma-separated, never summed. Tick Editable values for a free-text input (e.g. Voice of Business, Pricing Action reason).</small>
      </div>
      <!-- Year scope (client mode): aggregate only the newest year's rows for
           this measure — Sales = 2026 sales instead of every year summed. The
           year column rides along in the flat rows (compute payload + a hidden
           helper measure in the saved payload for published recomputes). -->
      <div class="control-group" *ngIf="isTableMatrixDrilldown && isDrillClientMode">
        <label>Year scope</label>
        <ng-select
          #ssWfMeasureYearScope
          class="ss-select"
          [class.is-open]="!!ssWfMeasureYearScope.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="drillMeasureYearScopes"
          bindLabel="label"
          bindValue="value"
          [clearable]="true"
          [ngModel]="measure.yearScope || 'all'"
          (ngModelChange)="onDrillMeasureYearScopeChange(measure, $event)"
        ></ng-select>
        <ng-container *ngIf="measure.yearScope === 'latest'">
          <label>Year column</label>
          <ng-select
            #ssWfMeasureYearField
            class="ss-select"
            [class.is-open]="!!ssWfMeasureYearField.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="columnsForDrillRow(measure)"
            [clearable]="true"
            [searchable]="true"
            [loading]="isDrillRowLoading(measure)"
            [disabled]="!drillRowHasDataset(measure)"
            placeholder="Column holding the year (e.g. fiscal_year)"
            [ngModel]="measure.yearField || null"
            (ngModelChange)="measure.yearField = $event || ''; onDrillMeasureChange()"
          ></ng-select>
          <small class="control-hint">Only the rows of the newest year in the data (e.g. 2026) feed this measure — at every level, in every pivot column and in the Total block. The header's info icon shows the year used. Re-generate after changing.</small>
          <label class="checkbox-label" matTooltip="The year column is only used to pick the newest year's rows and is hidden by default. Tick to show it as its own column in the table.">
            <input
              type="checkbox"
              [ngModel]="measure.showYearColumn === true"
              (ngModelChange)="measure.showYearColumn = $event ? true : undefined; onDrillMeasureChange()"
            />
            Show year column in table
          </label>
        </ng-container>
      </div>
      <!-- Header text inside the right-hand Total block only (Totals = Right /
           Both): e.g. "Revenue" over the Sales summed across the verticals,
           while every vertical's own column still reads "Sales". -->
      <div class="control-group" *ngIf="isTableMatrixDrilldown && measure.kind !== 'text' && measure.pivot !== false && measure.total && drillColumnDimensions.length && host.fieldEnabled('measures', 'rowTotalHeader')">
        <label>{{ host.fieldLabel('measures', 'rowTotalHeader', 'Header in Total block') }}</label>
        <input
          type="text"
          class="ss-input"
          [placeholder]="measure.label || measure.field || 'Same as the measure'"
          [ngModel]="measure.rowTotalHeader || ''"
          (ngModelChange)="measure.rowTotalHeader = $event; onDrillMeasureChange()"
        />
        <small class="control-hint">Only the column inside the trailing Total block is renamed (e.g. Revenue); the per-vertical columns keep the measure name. Re-generate after changing.</small>
      </div>
      <div class="control-group" *ngIf="isTableMatrixDrilldown && isDrillClientMode">
        <label class="checkbox-label" matTooltip="Compute this measure for formulas (and totals maths) but don't show it as a column — e.g. Total Cost behind SSA % / Margin %.">
          <input
            type="checkbox"
            [ngModel]="measure.hidden === true"
            (ngModelChange)="measure.hidden = $event ? true : undefined; onDrillMeasureChange()"
          />
          Hide column (formula input only)
        </label>
      </div>
      <div class="control-group" *ngIf="isTableMatrixDrilldown && drillColumnDimensions.length">
        <label class="checkbox-label" matTooltip="On: one column per pivot value (2024 Net Sales | 2025 Net Sales …). Off: a single column for this measure (e.g. Cost increase %, AOP Target).">
          <input
            type="checkbox"
            [ngModel]="measure.pivot !== false"
            (ngModelChange)="measure.pivot = $event ? undefined : false; onDrillMeasureChange()"
          />
          Split by pivot column
        </label>
      </div>
      <div class="control-group" *ngIf="host.fieldEnabled('measures', 'editable')">
        <label class="checkbox-label" matTooltip="Renders this measure's leaf-row cells as editable inputs (parents and the grand total re-sum as you edit); Save sends the changed values through the table's save pipeline">
          <input
            type="checkbox"
            [ngModel]="measure.editable"
            (ngModelChange)="measure.editable = $event; onDrillMeasureChange()"
          />
          {{ host.fieldLabel('measures', 'editable', 'Editable values') }}
        </label>
      </div>
      <!-- Edit control for the cell: a number / text input, or a dropdown
           over CUSTOM values (they need not exist in the data) and/or the
           values this column already holds in the table. -->
      <div class="control-group" *ngIf="measure.editable && host.fieldEnabled('measures', 'editor')">
        <label>{{ host.fieldLabel('measures', 'editor', 'Edit control') }}</label>
        <ng-select
          #ssWfMeasureEditor
          class="ss-select"
          [class.is-open]="!!ssWfMeasureEditor.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="drillEditorKinds"
          bindLabel="label"
          bindValue="value"
          [clearable]="true"
          [ngModel]="measure.editor || 'input'"
          (ngModelChange)="measure.editor = $event === 'dropdown' ? 'dropdown' : undefined; onDrillMeasureChange()"
        ></ng-select>
      </div>
      <div class="control-group" *ngIf="measure.editable && measure.editor === 'dropdown' && host.fieldEnabled('measures', 'editor')">
        <label>{{ host.fieldLabel('measures', 'dropdownOptions', 'Dropdown values') }}</label>
        <input
          type="text"
          [ngModel]="measure.dropdownOptions"
          (ngModelChange)="measure.dropdownOptions = $event; onDrillMeasureChange()"
          [placeholder]="measure.kind === 'text' ? 'e.g. Approved, Pending, Rejected' : 'e.g. 0, 25, 50, 100'"
        />
        <small class="control-hint">
          Comma-separated custom values — they don't have to exist in the data. Leave blank to list only the values from the table.<ng-container *ngIf="measure.kind !== 'text'"> A Number measure stores the pick as a number, so keep these numeric (or switch Value type to Text).</ng-container>
        </small>
        <label class="checkbox-label" matTooltip="Adds every value this column currently holds in the table to the dropdown, after the custom values">
          <input
            type="checkbox"
            [ngModel]="measure.dropdownFromData === true"
            (ngModelChange)="measure.dropdownFromData = $event ? true : undefined; onDrillMeasureChange()"
          />
          {{ host.fieldLabel('measures', 'dropdownFromData', 'Also list values from the table') }}
        </label>
      </div>
      <!-- Which tree levels the editable cell (input / dropdown) shows on.
           Multi-select over the dimensions: Level 1 only, Level 2 only, or
           any mix. Empty = default (Text: every level; Number: leaf rows). -->
      <div class="control-group" *ngIf="measure.editable && drillDownDimensions.length && host.fieldEnabled('measures', 'editableLevels')">
        <label>{{ host.fieldLabel('measures', 'editableLevels', 'Editable on levels') }}</label>
        <ng-select
          #ssWfMeasureLevels
          class="ss-select"
          [class.is-open]="!!ssWfMeasureLevels.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="drillEditableLevelOptions"
          bindLabel="label"
          bindValue="value"
          [multiple]="true"
          [closeOnSelect]="false"
          [clearable]="true"
          placeholder="Default (Text: every level · Number: leaf rows)"
          [ngModel]="measure.editableLevels"
          (ngModelChange)="onDrillMeasureLevelsChange(measure, $event)"
        ></ng-select>
        <small class="control-hint">
          Pick the levels whose rows get the editor — e.g. Level 1 only for a top-level dropdown. Total rows never edit. A Number measure edited on a parent keeps that value until saved; its own parents still re-sum.
        </small>
      </div>
      <!-- GENERIC cell configuration for this measure's cells — the ONE
           editor shared with the Normal / Editable table columns
           (table-cell-config.ts): display kind (badge / data bar / heat map
           / trend…), colours, conditional rules, highlight, prefix / suffix,
           tooltip. The dropdown values stay on the Edit control above. -->
      <div class="control-group">
        <app-table-cell-config-editor
          title="Cell formatting"
          [showNavigation]="false"
          [config]="measure.cellConfig"
          [fields]="drillRuleFields"
          [valueKind]="measure.kind === 'text' ? 'text' : 'number'"
          (configChange)="onDrillMeasureCellConfigChange(measure, $event)"
        ></app-table-cell-config-editor>
      </div>
    </div>
  </div>
</div>

<!-- ══ Merged Drill-Down + Pivot extras (Table & Matrix drill-down type) ══
     Data computation mode, pivot column dimension(s) and formula columns.
     All optional — a plain hierarchy needs none of them. -->
<div class="wf-section wf-section-plain drill-pivot-config" *ngIf="isTableMatrixDrilldown">
  <div class="control-group">
    <label>{{ host.fieldLabel('tableMatrix', 'dataMode', 'Data computation') }}</label>
    <ng-select
      #ssDrillMode
      class="ss-select"
      [class.is-open]="!!ssDrillMode.isOpen?.()"
      appendTo="body"
      ngClass="ss-dropdown-panel"
      [items]="drillDataModes"
      bindLabel="label"
      bindValue="value"
      [clearable]="true"
      [ngModel]="drillDataMode"
      (ngModelChange)="onDrillDataModeChange($event)"
    ></ng-select>
    <small class="control-hint" *ngIf="drillDataMode === 'client'">The joined flat rows are fetched and the hierarchy, pivot columns and formula columns are built in the browser — this enables multiple datasets, pivot columns and formulas.</small>
    <small class="control-warning" *ngIf="drillDataMode === 'server'">Backend tree (legacy): pivot column dimensions are ignored and every level / measure should come from the primary dataset. Switch to client-side to use them; formula columns still work.</small>
  </div>

  <!-- Totals placement: bottom grand-total row and/or the right-hand Total
       block across the pivot column values (Figma "TOTAL | Rev | Price %"). -->
  <div class="control-group" *ngIf="host.fieldEnabled('tableMatrix', 'totalsPlacement')">
    <label>{{ host.fieldLabel('tableMatrix', 'totalsPlacement', 'Totals') }}</label>
    <ng-select
      #ssDrillTotals
      class="ss-select"
      [class.is-open]="!!ssDrillTotals.isOpen?.()"
      appendTo="body"
      ngClass="ss-dropdown-panel"
      [items]="drillTotalsPlacements"
      bindLabel="label"
      bindValue="value"
      [clearable]="true"
      [ngModel]="drillTotalsPlacement"
      (ngModelChange)="onDrillTotalsPlacementChange($event)"
    ></ng-select>
    <small class="control-hint" *ngIf="drillTotalsPlacement === 'right' || drillTotalsPlacement === 'both' || drillTotalsPlacement === 'left' || drillTotalsPlacement === 'both-left'">The Total block carries the measures ticked "Show in Grand Total", each summed across the pivot column values, plus any formula column whose inputs are all ticked (evaluated on those totals, like the bottom row); text attributes stay out. {{ drillTotalsPlacement === 'left' || drillTotalsPlacement === 'both-left' ? 'It sits directly after the first-level column, ahead of the pivot column blocks.' : 'It trails the pivot column blocks.' }} It needs a pivot column dimension{{ drillDataMode === 'client' ? '' : ' and client-side computation' }}.</small>
    <small class="control-hint" *ngIf="drillTotalsPlacement === 'none' || drillTotalsPlacement === 'right' || drillTotalsPlacement === 'left'">The bottom total row is hidden.</small>
  </div>

  <!-- Pivot column dimension(s): every measure fans out per distinct value. -->
  <div class="drill-down-dimensions wf-field-drop-zone" (dragover)="host.allowFieldDrop($event)" (drop)="onDrillColumnDimensionFieldDrop($event)">
    <header class="wf-section-header wf-steps-head">
      <h3>{{ host.fieldLabel('tableMatrix', 'columnDimensions', 'Pivot column dimension(s)') }}</h3>
      <button type="button" mat-stroked-button (click)="addDrillColumnDimension()">+ Add Column</button>
    </header>
    <small class="control-hint">Optional (the Pivot Table's column fields). Every measure fans out per distinct value of this column — e.g. Fiscal Year → 2024 Net Sales | 2025 Net Sales | … — with a grand total per column. Leave empty for a plain hierarchy.</small>

    <div class="wf-step-card wf-field-drop-zone" *ngFor="let dimension of drillColumnDimensions; let i = index" (dragover)="host.allowFieldDrop($event)" (drop)="onDrillColumnDimensionFieldDrop($event, dimension)">
      <div class="wf-step-head">
        <span class="wf-step-index">{{ i + 1 }}</span>
        <ng-select
          #ssWfColDimField
          class="ss-select wf-step-field-select"
          [class.is-open]="!!ssWfColDimField.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="columnsForDrillRow(dimension)"
          [clearable]="true"
          [searchable]="true"
          [loading]="isDrillRowLoading(dimension)"
          [disabled]="!drillRowHasDataset(dimension)"
          placeholder="Column whose values become columns"
          [ngModel]="dimension.field || null"
          (ngModelChange)="dimension.field = $event || ''; onDrillColumnDimensionChange()"
        ></ng-select>
        <input
          type="text"
          class="wf-step-label"
          [ngModel]="dimension.label"
          (ngModelChange)="dimension.label = $event; onDrillColumnDimensionChange()"
          placeholder="Display name"
        />
        <div class="wf-step-actions">
          <button type="button" mat-icon-button class="row-remove-button" (click)="removeDrillColumnDimension(dimension)" matTooltip="Remove pivot column" aria-label="Remove pivot column">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>
      <div class="control-row">
        <div class="control-group">
          <label>Dataset</label>
          <ng-select
            #ssWfColDimDataset
            class="ss-select"
            [class.is-open]="!!ssWfColDimDataset.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="host.entityOptions"
            [loading]="host.entitiesLoading"
            [clearable]="true"
            [searchable]="true"
            placeholder="Same as primary"
            [ngModel]="dimension.dataset || host.drillDataset || null"
            (ngModelChange)="onDrillRowDatasetChange(dimension, $event, 'columnDimension')"
          ></ng-select>
        </div>
        <div class="control-group">
          <label>Column order (optional)</label>
          <input
            type="text"
            [ngModel]="dimension.valueOrder"
            (ngModelChange)="dimension.valueOrder = $event; onDrillColumnDimensionChange()"
            placeholder="e.g. Mining, Marine, Oil &amp; Gas, Rail (blank = design preset)"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Formula columns — computed per tree row from its aggregated measures
       (clone of the Bar Formula Builder, bound to the drill-down state). -->
  <div class="dvm-formula-builder" *ngIf="formulaBuilderEnabled">
    <button type="button" class="dvm-formula-head" (click)="drillFormulaAccordionOpen = !drillFormulaAccordionOpen">
      <span><mat-icon class="dvm-formula-head-icon">functions</mat-icon> {{ host.fieldLabel('tableMatrix', 'computedColumns', 'Formula columns') }} <span class="wf-step-count">{{ drillComputedColumns.length }}</span></span>
      <mat-icon>{{ drillFormulaAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
    </button>

    <div class="dvm-formula-body" *ngIf="drillFormulaAccordionOpen">
      <p class="dvm-formula-hint">Computed for every row of the tree from that row's aggregated measures (a ratio at a parent divides the sums) and re-evaluated as values are edited. Reference measures by their header text — e.g. <code>([Net Sales] - [Total Cost]) / [Net Sales] * 100</code>. Operators + - * / and parentheses; IF(cond, then, else), abs/round/min/max.</p>

      <div class="dvm-formula-templates" *ngIf="formulaTemplates.length">
        <small class="control-hint">{{ host.formulaBuilderConfig.templatesLabel || 'Quick templates:' }}</small>
        <button
          type="button"
          class="dvm-formula-chip"
          *ngFor="let tpl of formulaTemplates"
          [title]="tpl.description"
          (click)="applyDrillFormulaTemplate(tpl)"
        >{{ tpl.label }}</button>
      </div>

      <div class="dvm-formula-list" *ngIf="drillComputedColumns.length">
        <div class="dvm-formula-item" *ngFor="let col of drillComputedColumns">
          <div class="dvm-formula-item-info">
            <strong>{{ col.name }}</strong>
            <small>= {{ col.expression }}</small>
            <small *ngIf="col.after" class="control-hint">placed after: {{ drillFormulaAnchorLabel(col.after) }}</small>
          </div>
          <button type="button" mat-icon-button class="bar-refline-remove row-remove-button" (click)="removeDrillComputedColumn(col.name)" matTooltip="Remove column"><mat-icon>close</mat-icon></button>
        </div>
      </div>

      <div class="dvm-formula-add">
        <input type="text" [(ngModel)]="newDrillColumnName" [placeholder]="host.formulaBuilderConfig.namePlaceholder || 'Column name'" />
        <input type="text" [(ngModel)]="newDrillColumnExpression" placeholder="Expression (e.g. [Net Sales] / [Net Quantity])" />
        <!-- Column position: after which measure / formula this column shows
             (e.g. SSA % right after Net Sales, before AOP Target). -->
        <ng-select
          #ssDrillFormulaAfter
          class="ss-select"
          [class.is-open]="!!ssDrillFormulaAfter.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="drillFormulaAnchorOptions"
          bindLabel="label"
          bindValue="value"
          [clearable]="true"
          [searchable]="true"
          placeholder="Place after…"
          [(ngModel)]="newDrillColumnAfter"
        ></ng-select>
        <button type="button" class="add-step-button" mat-stroked-button (click)="addDrillComputedColumn()"><mat-icon>add</mat-icon> {{ host.formulaBuilderConfig.addLabel || 'Add column' }}</button>
      </div>
      <small class="control-warning" *ngIf="newDrillColumnError">{{ newDrillColumnError }}</small>
      <small class="control-hint" *ngIf="!drillDownMeasures.length">Add at least one measure first — formulas reference measures.</small>
      <!-- The names this table's formulas may use (measure labels + raw
           fields + earlier formula columns) — click to insert [Name]. -->
      <div class="dvm-formula-templates" *ngIf="drillFormulaColumns.length">
        <small class="control-hint">Available names (click to insert):</small>
        <button type="button" class="dvm-formula-chip" *ngFor="let name of drillFormulaColumns" (click)="insertDrillFormulaRef(name)" title="Insert into the expression">[{{ name }}]</button>
      </div>
    </div>
  </div>
</div>

<!-- Column order: ONE list of every output column (measure blocks +
     formula columns) in render order. Move up / down here overrides the
     measure order and the formulas' "placed after" anchors; it applies to
     the client-side tree (the backend tree fixes its own key order). -->
<div class="dvm-formula-builder" *ngIf="drillDataMode === 'client' && host.fieldEnabled('tableMatrix', 'columnOrder')">
  <button type="button" class="dvm-formula-head" (click)="drillColumnOrderAccordionOpen = !drillColumnOrderAccordionOpen">
    <span><mat-icon class="dvm-formula-head-icon">view_column</mat-icon> {{ host.fieldLabel('tableMatrix', 'columnOrder', 'Column order') }} <span class="wf-step-count">{{ drillColumnOrderItems.length }}</span><small *ngIf="drillColumnOrder.length" class="control-hint"> (custom)</small><small *ngIf="!drillColumnOrder.length && drillColumnOrderPreset.length" class="control-hint"> (design preset)</small></span>
    <mat-icon>{{ drillColumnOrderAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
  </button>

  <div class="dvm-formula-body" *ngIf="drillColumnOrderAccordionOpen">
    <p class="dvm-formula-hint">Every column the table renders, top to bottom = left to right. Move a row to change where it shows; a measure split by the pivot column moves together with all its per-value columns. This overrides the default order — Reset returns to it.<ng-container *ngIf="drillColumnOrderPreset.length"> By default the columns follow the design preset automatically: whichever of <em>{{ drillColumnOrderPresetLabel }}</em> the table has render in that order, any other column follows.</ng-container><ng-container *ngIf="!drillColumnOrderPreset.length"> The default is the measure order plus the formulas' "placed after" anchors.</ng-container></p>
    <small class="control-hint" *ngIf="!drillColumnOrderItems.length">Add at least one measure first.</small>

    <div class="dvm-formula-list" *ngIf="drillColumnOrderItems.length">
      <div class="dvm-formula-item" *ngFor="let col of drillColumnOrderItems; let i = index; let first = first; let last = last">
        <div class="dvm-formula-item-info">
          <strong><span class="wf-step-index">{{ i + 1 }}</span> {{ col.key }}</strong>
          <small>{{ col.kind === 'formula' ? 'formula column' : (col.split ? 'one column per pivot value' : 'single column') }}</small>
        </div>
        <div class="wf-step-actions">
          <button type="button" mat-icon-button [disabled]="first" (click)="moveDrillColumn(i, -1)" matTooltip="Move up" aria-label="Move column up"><mat-icon>arrow_upward</mat-icon></button>
          <button type="button" mat-icon-button [disabled]="last" (click)="moveDrillColumn(i, 1)" matTooltip="Move down" aria-label="Move column down"><mat-icon>arrow_downward</mat-icon></button>
        </div>
      </div>
    </div>

    <button type="button" mat-stroked-button class="add-step-button" *ngIf="drillColumnOrder.length" (click)="resetDrillColumnOrder()"><mat-icon>restart_alt</mat-icon> Reset to default order</button>
  </div>
</div>

<!-- Drill-Down Table: Format accordions (registry `sections`-gated). The
     Table & Matrix drill-down variant gets its own accordion in its block. -->
<ng-container *ngIf="isLegacyDrillDown" [ngTemplateOutlet]="chartFormatAccordion"></ng-container>
</ng-container>
